import React from 'react';
import PropTypes from 'prop-types';
import './WaitlistUser.scss';

import cn from 'classnames';
import moment from 'moment';

import { ReactComponent as InstagramIcon } from '../../static/images/social_icons/instagram.svg';
import { ReactComponent as TiktokIcon } from '../../static/images/social_icons/tiktok.svg';
import { ReactComponent as YoutubeIcon } from '../../static/images/social_icons/youtube.svg';
import { ReactComponent as PinterestIcon } from '../../static/images/social_icons/pinterest.svg';
import { ReactComponent as SubstackIcon } from '../../static/images/social_icons/substack.svg';
import { ReactComponent as LinkIcon } from '../../static/images/social_icons/link.svg';
import Tooltip from '../General/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

import { formatCityCountryFromLocation } from '../../helpers/geo_helpers';
import { parseJson } from '../../helpers/helpers';
import { getWaitlistUserSocialLinks, getWaitlistLegalNameIfDifferentFromName, parseWaitlistAutomationData } from '../../helpers/waitlist_helpers';

const SOCIAL_ICONS = {
  instagram: InstagramIcon,
  tiktok: TiktokIcon,
  youtube: YoutubeIcon,
  pinterest: PinterestIcon,
  substack: SubstackIcon,
  website: LinkIcon
};

const WaitlistUser = props => {
  const { waitlistUser } = props;

  const parsed = parseJson(waitlistUser.location);
  const formattedWaitlistUserLocation = parsed ? formatCityCountryFromLocation(parsed) : '-';

  const { automationData, gpt, acceptConfidence, rejectConfidence } = parseWaitlistAutomationData(waitlistUser);

  const gptRecommendation =
    gpt?.scammerLikelihood > 60
      ? {
          className: 'scammer',
          label: 'Scammer',
          text: `AI: Scammer (${gpt.scammerLikelihood}%). ${gpt.reason}`,
          rating: gpt.rating,
          Icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
          action: () => props.markApplicationAsScammer(waitlistUser, gpt.reason)
        }
      : waitlistUser.needsReview || gpt?.requiresManualReview === true
      ? {
          className: 'manual-review',
          label: waitlistUser.needsReview && automationData?.gate === 'country_raised_threshold' ? 'Country review' : 'Review',
          text: `Needs Manual Review: ${waitlistUser.automationDecisionReason || gpt?.reason}`,
          rating: gpt?.rating,
          Icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
          action: () => props.setSelectedApplicationId(waitlistUser.id)
        }
      : acceptConfidence > 60
      ? {
          className: 'accept',
          label: 'Accept',
          text: `AI: Accept (${acceptConfidence}%). ${gpt.reason}`,
          rating: gpt.rating,
          Icon: <FontAwesomeIcon icon={faCheck} />,
          action: () => props.setSelectedApplicationId(waitlistUser.id)
        }
      : rejectConfidence > 60
      ? {
          className: 'reject',
          label: 'Reject',
          text: `AI: Reject (${rejectConfidence}%). ${gpt.reason}`,
          rating: gpt.rating,
          Icon: <FontAwesomeIcon icon={faTimes} />,
          action: () => props.rejectWaitlistApplication(waitlistUser)
        }
      : automationData?.missingData
      ? {
          className: 'no-data',
          label: 'No Data',
          text: `AI: ${automationData.reason}`,
          rating: null,
          Icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
          action: () => props.setSelectedApplicationId(waitlistUser.id)
        }
      : null;

  const legalName = getWaitlistLegalNameIfDifferentFromName(waitlistUser);

  return (
    <div
      className={cn('waitlist-user row', { accepted: waitlistUser.isAccepted, rejected: waitlistUser.isRejected })}
      key={waitlistUser.id}
      onClick={() => props.setSelectedApplicationId(waitlistUser.id)}
    >
      <div className='cell created'>{moment(waitlistUser.createdAt).format('MMM Do')}</div>
      <div className='cell name waitlist-user-name'>
        {!!waitlistUser.ShopperUser_id && <span className='waitlist-user-tag'>Shopper</span>}
        <div className='waitlist-user-name-stack'>
          <span>{waitlistUser.name}</span>
          {legalName && <span className='waitlist-user-legal-name'>{legalName}</span>}
        </div>
      </div>
      <div className='cell email'>{waitlistUser.email || '-'}</div>
      <div className='cell links icon-container'>
        {getWaitlistUserSocialLinks(waitlistUser).map(({ key, url, authenticated }) => {
          const Icon = SOCIAL_ICONS[key];
          const message = [url, authenticated && 'Authenticated'].filter(Boolean).join(' · ');
          const icon = (
            <div className={cn('waitlist-user-social-icon', { disabled: !url && !authenticated, authenticated })}>
              {url ? (
                <a href={url} target='_blank' rel='noopener noreferrer' onClick={event => event.stopPropagation()}>
                  <Icon />
                </a>
              ) : (
                <span>
                  <Icon />
                </span>
              )}
            </div>
          );

          if (!message) return <div key={key}>{icon}</div>;

          return (
            <Tooltip key={key} message={message}>
              {icon}
            </Tooltip>
          );
        })}
      </div>
      <div className='cell followers'>{waitlistUser.largestFollowing}</div>
      <div className='cell referred'>
        {waitlistUser.referringUser?.id ? (
          <a href={`https://shopmy.us/${waitlistUser.referringUser.username}`} target='_blank' rel='noopener noreferrer'>
            {waitlistUser.referringUser.name}
          </a>
        ) : waitlistUser.referringBrand?.id ? (
          `${waitlistUser.referringBrand.name}`
        ) : (
          '-'
        )}
      </div>
      <div className='cell location'>{formattedWaitlistUserLocation}</div>
      <div className='cell status nowrap'>
        {!waitlistUser.isAccepted && !waitlistUser.isRejected ? (
          <>
            <Tooltip message='Open Review'>
              <FontAwesomeIcon
                icon={faCheck}
                onClick={e => {
                  e.stopPropagation();
                  props.setSelectedApplicationId(waitlistUser.id);
                }}
              />
            </Tooltip>
            <Tooltip message='Reject'>
              <FontAwesomeIcon
                icon={faTimes}
                onClick={e => {
                  e.stopPropagation();
                  props.rejectWaitlistApplication(waitlistUser);
                }}
              />
            </Tooltip>
            <Tooltip message='Mark as Scammer'>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                onClick={e => {
                  e.stopPropagation();
                  props.markApplicationAsScammer(waitlistUser);
                }}
              />
            </Tooltip>
          </>
        ) : waitlistUser.isAccepted ? (
          <span className='accepted'>Accepted</span>
        ) : (
          <span className='rejected'>Rejected</span>
        )}
      </div>

      {!!gptRecommendation && (
        <Tooltip message={gptRecommendation.text}>
          <div
            className={`waitlist-user-ai-recommendation ${gptRecommendation.className}`}
            onClick={e => {
              e.stopPropagation();
              gptRecommendation.action();
            }}
          >
            {gptRecommendation.Icon}
            <span className='ai-rec-label'>{gptRecommendation.label}</span>
            {gptRecommendation.rating && <span className='ai-rec-rating'>{gptRecommendation.rating}/5</span>}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

WaitlistUser.propTypes = {
  waitlistUser: PropTypes.object.isRequired,
  setSelectedApplicationId: PropTypes.func.isRequired,
  rejectWaitlistApplication: PropTypes.func.isRequired,
  markApplicationAsScammer: PropTypes.func.isRequired
};

export default WaitlistUser;
